import { UIIcon } from '../../../config/icons';
import { $on, $trigger, YBlurEvent, YEventHandlers, YFocusEvent, YInputEvent, YStateEvent, YValueEvent } from '../../y/events';
import { YInputOptions, YInput } from '../../y/form';
import { $el } from '../elements';
import { onKey, UIKey } from '../key';
import { UIClassesSpec, UIRelPos, UIValue } from '../types';
import { UIView } from '../view';
import { UIDataAdapter } from './adapter';
import { UIDropdown, valueDropdownGenerator } from './dropdown';
import { UIBaseInput, UIBaseInputOptions } from './input-base';
import { UIList } from './list';

export class UIChip extends UIView {
    $handlers: YEventHandlers<{ remove: void }>;

    constructor(text: string, classes?: UIClassesSpec, icon?: UIIcon) {
        super('div', ['ui-chip', classes]);

        const remove = $el.ICON(UIIcon.XMARK | UIIcon.WEIGHT_SOLID)
        this.ui_add([
            $el.SPAN('r-text', [
                icon && $el.ICON(icon, 'r-icon'),
                text
            ]),
            remove
        ]);

        remove.addEventListener('click', ev => {
            $trigger(this, 'remove');
            ev.stopPropagation();
            ev.preventDefault();
        });
    }
}

interface UIChipInputOptions extends UIBaseInputOptions {
    to_chip?: (text: string) => (string | undefined);
    autocomplete?: (term: string) => Promise<UIValue<string>[]>;
    space_break?: boolean;
    clear_button?: boolean;
}

export class UIChipInput extends UIBaseInput<string[]> {
    private _text: string;
    private _chips: UIDataAdapter<string, UIChip>;
    private _input: HTMLSpanElement;
    private _to_chip: (text: string) => (string | undefined);

    constructor(options: UIChipInputOptions = {}) {
        const div = $el.DIV();

        if(options.clear_button) {
            const button = $el.ICON(UIIcon.TRASH_CAN, 'u-clickable');
            options.r_deco = button;
            button.addEventListener('click', () => {
                this.value = [];
                input.textContent = '';
                this._text = '';
            });
        }

        super(options, div);
        this.classList.add('ui-chip-input');

        if(options.placeholder)
            div.appendChild($el.DIV('r-placeholder', options.placeholder));

        this._to_chip = options.to_chip || (s => s.trim());

        const chips = this._chips = new UIDataAdapter((m, e) => {
            const chip = new UIChip(m);
            $on(chip, 'remove', () => {
                chips.remove(e);
                $trigger(this, 'value', chips.data);
                input.focus();
                this.fixDesign();
            });
            return chip;
        });

        const input = this._input = $el.SPAN('r-input');
        const wrapper = $el.SPAN('r-wrapper', input);
        input.contentEditable = 'true';

        div.ui_add([
            new UIList(chips, { classes: 'r-chips' }),
            wrapper
        ]);

        let popup: UIDropdown | undefined
        let token: {};
        const autocomplete = options.autocomplete;

        input.addEventListener('blur', () => {
            this.classList.remove('f-focus');
            $trigger(this, 'blur');
        });

        input.addEventListener('focus', () => {
            this.classList.add('f-focus');
            $trigger(this, 'focus');
        });

        input.addEventListener('input', () => {
            this.setDirty(true);
            const text = this._to_chip(this._text = input.textContent || '');
            if(autocomplete) {
                if(popup) {
                    popup.close();
                    popup = undefined;
                }

                if(text) {
                    const this_token = token = {};
                    autocomplete(text).then(options => {
                        if(this_token != token) return;
                        popup = new UIDropdown(options, valueDropdownGenerator);
                        popup.on_choose = v => { this._commitChip(v.key) };
                        popup.open(this, { position: UIRelPos.Below | UIRelPos.End, adjust_size: true, match_size: true, capture_mouse: true, can_flip: true });
                    });
                }
            }
            this.fixDesign();
        });

        onKey(input, e => {
            if(e.key === UIKey.Enter || (options.space_break && e.key === UIKey.Space)) {
                if(popup) {
                    if(popup.has_selection) return true;
                    popup.close();
                }

                this.setDirty(true);
                this.commit();

                return true;
            } else if(e.key == UIKey.Backspace) {
                if(!input.textContent)
                    return chips.removeAt(chips.count - 1);
            }

            return;
        });

        input.addEventListener('paste', e => {
            e.preventDefault();
            const text = e.clipboardData!.getData('text/plain');
            document.execCommand('insertText', false, text);
            this.fixDesign();
        });

        div.addEventListener('click', e => {
            if(e.target == div) {
                input.focus();
            };
        });

        this._text = '';
        this.value = [];
    }

    commit() {
        const chip_text = this._to_chip(this._input.textContent || '');
        if(chip_text)
            this._commitChip(chip_text);
    }

    push(text: string) {
        this._commitChip(text);
    }

    private _commitChip(text: string) {
        this._chips.push(text);
        $trigger(this, 'value', this._chips.data);
        this._input.textContent = '';
        this._text = '';
        this.doCheck();
        this.fixDesign();
    }

    get value(): string[] {
        return this._chips.data;
    }

    set value(v: string[] | undefined) {
        v = v || [];
        this._chips.data = v;
        $trigger(this, 'value', v);
        this.doCheck();
        this.fixDesign();
    }

    get is_empty() {
        return !this.value.length && !this._text;
    }

    protected doCheck() {
        this.setOkay(!(this.required && !this.value.length));
    }
}

export interface UIChipsOptions<Value> extends YInputOptions {
    to_chip: (text: Value) => string;
    classes?: UIClassesSpec;
}

export class UIChips<Value> extends UIView implements YInput<Value[]> {
    $handlers: YEventHandlers<YStateEvent & YInputEvent & YValueEvent<Value[]> & YBlurEvent & YFocusEvent>;

    private _chips: UIDataAdapter<Value, UIChip>;

    okay!: boolean;
    private _required: boolean;
    private _disabled: boolean;

    dirty: boolean;

    constructor(options: UIChipsOptions<Value>) {
        super('div', ['ui-chips', 'i-input', options.classes]);

        this._required = !!options.required;
        this._disabled = !!options.disabled;
        this.dirty = false;

        const chips = this._chips = new UIDataAdapter((m, e) => {
            const chip = new UIChip(options.to_chip(m), 'm-full');
            $on(chip, 'remove', () => {
                chips.remove(e);
                $trigger(this, 'value', chips.data);
            });
            return chip;
        });

        this.ui_add([
            new UIList(chips, { classes: 'r-chips' })
        ]);

        this.value = [];
    }

    get value(): Value[] {
        return this._chips.data;
    }

    set value(v: Value[] | undefined) {
        v = v || [];
        this._chips.data = v;
        $trigger(this, 'value', v);
        this.doCheck();
    }

    get is_empty() {
        return !this.value.length;
    }

    setOkay(o: boolean) {
        if(o === this.okay) return;

        this.okay = o;
        $trigger(this, 'state');
    }

    get disabled() {
        return this._disabled;
    }

    set disabled(d: boolean) {
        this._disabled = d;
        this.classList.toggle('f-disabled', d);
    }

    get required() {
        return this._required;
    }

    set required(r: boolean) {
        this._required = r;
        this.doCheck();
    }

    doCheck() {
        this.setOkay(!(this.required && !this.value.length));
    }
}